import { createTheme } from '@mui/material/styles';
import {
  backgroundColors,
  brandColors,
  textColors,
} from '../../../shared/theme/colors.js';

const theme = createTheme({
  palette: {
    primary: brandColors.primary,
    secondary: brandColors.secondary,
    background: {
      default: backgroundColors.default,
      paper: backgroundColors.paper,
    },
    text: {
      primary: textColors.primary,
      secondary: textColors.secondary,
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    // MOBILE-FIRST: базовые стили для мобильных, @media (min-width) для desktop
    h1: {
      fontWeight: 700,
      fontSize: '1.75rem', // mobile base
      letterSpacing: '-0.02em',
      '@media (min-width: 600px)': {
        fontSize: '2.5rem', // desktop
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.5rem', // mobile base
      letterSpacing: '-0.015em',
      '@media (min-width: 600px)': {
        fontSize: '2rem', // desktop
      },
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.25rem', // mobile base
      letterSpacing: '-0.01em',
      '@media (min-width: 600px)': {
        fontSize: '1.75rem', // desktop
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.15rem', // mobile base
      letterSpacing: '-0.01em',
      '@media (min-width: 600px)': {
        fontSize: '1.5rem', // desktop
      },
    },
    h5: {
      fontWeight: 500,
      fontSize: '1rem', // mobile base
      '@media (min-width: 600px)': {
        fontSize: '1.25rem', // desktop
      },
    },
    h6: {
      fontWeight: 500,
      fontSize: '0.95rem', // mobile base
      '@media (min-width: 600px)': {
        fontSize: '1.1rem', // desktop
      },
    },
    // MOBILE-FIRST: читаемость
    body1: {
      fontSize: '0.95rem', // mobile base
      lineHeight: 1.5,
      '@media (min-width: 600px)': {
        fontSize: '1rem', // desktop
        lineHeight: 1.6,
      },
    },
    body2: {
      fontSize: '0.85rem', // mobile base
      lineHeight: 1.4,
      '@media (min-width: 600px)': {
        fontSize: '0.875rem', // desktop
        lineHeight: 1.5,
      },
    },
    caption: {
      fontSize: '0.7rem', // mobile base
      '@media (min-width: 600px)': {
        fontSize: '0.75rem', // desktop
      },
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  // Мобильные отступы (safe areas)
  spacing: 8, // базовый 8px
  components: {
    // Глобальные focus-visible стили для accessibility
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 600,
          // MOBILE-FIRST: базовые стили для мобильных
          padding: '10px 16px',
          fontSize: '0.9rem',
          minHeight: '44px', // WCAG тач-таргет
          // Accessibility: focus-visible стили
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: '#2C3E50',
            outlineOffset: '2px',
            boxShadow: '0 0 0 4px rgba(44, 62, 80, 0.2)',
          },
          // Более заметное нажатие на мобильных
          '&:active': {
            transform: 'scale(0.98)',
          },
          // Desktop: увеличенные отступы
          '@media (min-width: 600px)': {
            padding: '10px 20px',
            fontSize: '0.95rem',
            '&:active': {
              transform: 'none',
            },
          },
        },
        // Компактная кнопка для карточек
        sizeSmall: {
          // MOBILE-FIRST
          padding: '8px 12px',
          fontSize: '0.8rem',
          minHeight: '40px',
          borderRadius: 10,
          // Desktop
          '@media (min-width: 600px)': {
            padding: '6px 12px',
            minHeight: '36px',
          },
        },
        sizeLarge: {
          // MOBILE-FIRST
          padding: '14px 24px',
          fontSize: '1rem',
          minHeight: '52px',
          borderRadius: 12,
          // Desktop
          '@media (min-width: 600px)': {
            padding: '14px 28px',
            fontSize: '1.05rem',
          },
        },
        // Кнопка на всю ширину на мобильных
        fullWidth: {
          // MOBILE-FIRST
          padding: '12px 16px',
          // Desktop: стандартный паддинг
          '@media (min-width: 600px)': {
            padding: '10px 20px',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: 44x44px touch target
          minWidth: '44px',
          minHeight: '44px',
          padding: '10px',
          // Accessibility: focus-visible стили
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: '#2C3E50',
            outlineOffset: '2px',
            backgroundColor: 'rgba(44, 62, 80, 0.08)',
          },
          '&:active': {
            backgroundColor: 'rgba(0,0,0,0.08)',
          },
        },
        sizeSmall: {
          // MOBILE-FIRST: увеличенный для мобильных
          minWidth: '40px',
          minHeight: '40px',
          padding: '8px',
          // Desktop: компактнее
          '@media (min-width: 600px)': {
            minWidth: '36px',
            minHeight: '36px',
            padding: '6px',
          },
        },
        sizeLarge: {
          minWidth: '52px',
          minHeight: '52px',
          padding: '14px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: базовые стили
          borderRadius: 10,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          transition: 'all 0.2s ease',
          // Активное нажатие на мобильных
          '&:active': {
            transform: 'scale(0.99)',
            boxShadow: '0 1px 8px rgba(0,0,0,0.1)',
          },
          // Убираем hover эффекты на мобильных (нет hover)
          '@media (hover: hover)': {
            '&:hover': {
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)',
            },
          },
          // Desktop
          '@media (min-width: 600px)': {
            borderRadius: 12,
            '&:active': {
              transform: 'none',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            },
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST
          padding: '12px',
          '&:last-child': {
            paddingBottom: '12px',
          },
          // Desktop
          '@media (min-width: 600px)': {
            padding: '16px',
            '&:last-child': {
              paddingBottom: '16px',
            },
          },
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST
          padding: '8px 12px 12px',
          // Desktop
          '@media (min-width: 600px)': {
            padding: '8px 16px 16px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            minHeight: '48px',
            // MOBILE-FIRST: iOS - 16px предотвращает авто-зум
            fontSize: '16px',
          },
          '& .MuiInputLabel-root': {
            // MOBILE-FIRST
            fontSize: '0.95rem',
          },
          // Accessibility: focus-visible для input
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
            {
              borderWidth: '2px',
            },
          // Desktop: стандартные размеры
          '@media (min-width: 600px)': {
            '& .MuiInputBase-root': {
              fontSize: 'inherit',
            },
            '& .MuiInputLabel-root': {
              fontSize: '1rem',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: iOS - 16px предотвращает авто-зум
          '& input': {
            fontSize: '16px',
          },
          '@media (min-width: 600px)': {
            '& input': {
              fontSize: 'inherit',
            },
          },
        },
        input: {
          // MOBILE-FIRST
          padding: '12px 14px',
          // Desktop
          '@media (min-width: 600px)': {
            padding: '14px',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST
          height: '26px',
          fontSize: '0.75rem',
          borderRadius: 10,
          // Desktop
          '@media (min-width: 600px)': {
            height: '28px',
            fontSize: '0.8rem',
          },
        },
        sizeSmall: {
          // MOBILE-FIRST
          height: '22px',
          fontSize: '0.65rem',
          borderRadius: 8,
          // Desktop
          '@media (min-width: 600px)': {
            height: '24px',
            fontSize: '0.7rem',
          },
        },
        label: {
          // MOBILE-FIRST
          paddingLeft: '8px',
          paddingRight: '8px',
          // Desktop
          '@media (min-width: 600px)': {
            paddingLeft: '10px',
            paddingRight: '10px',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          '& .MuiToolbar-root': {
            // MOBILE-FIRST
            minHeight: '56px',
            paddingLeft: '12px',
            paddingRight: '12px',
            // Desktop
            '@media (min-width: 600px)': {
              minHeight: '64px',
              paddingLeft: '24px',
              paddingRight: '24px',
            },
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST
          minHeight: '56px',
          // Desktop
          '@media (min-width: 600px)': {
            minHeight: '64px',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: компактные отступы
          paddingLeft: '8px',
          paddingRight: '8px',
          // Средние экраны (>=400px)
          '@media (min-width: 400px)': {
            paddingLeft: '12px',
            paddingRight: '12px',
          },
          // Desktop: стандартные отступы
          '@media (min-width: 600px)': {
            paddingLeft: '24px',
            paddingRight: '24px',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          // MOBILE-FIRST: 85% ширины экрана
          width: '85vw',
          maxWidth: '320px',
          // Desktop: авто ширина
          '@media (min-width: 600px)': {
            width: 'auto',
            maxWidth: 'none',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          // MOBILE-FIRST: полноэкранный режим
          margin: '8px',
          width: 'calc(100% - 16px)',
          maxHeight: 'calc(100% - 16px)',
          // Desktop: стандартные отступы
          '@media (min-width: 600px)': {
            margin: '16px',
            width: 'auto',
            maxWidth: 600,
            maxHeight: 'calc(100% - 64px)',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST
          padding: '14px 16px',
          fontSize: '1.1rem',
          // Desktop
          '@media (min-width: 600px)': {
            padding: '16px 24px',
            fontSize: '1.25rem',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST
          padding: '8px 16px 16px',
          // Desktop
          '@media (min-width: 600px)': {
            padding: '8px 24px 20px',
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST
          padding: '8px 16px 12px',
          '& > :not(:first-of-type)': {
            marginLeft: '8px',
          },
          // Desktop
          '@media (min-width: 600px)': {
            padding: '8px 24px 16px',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: увеличенный touch target
          minHeight: '52px',
          paddingTop: '12px',
          paddingBottom: '12px',
          // Desktop
          '@media (min-width: 600px)': {
            minHeight: '48px',
            paddingTop: '8px',
            paddingBottom: '8px',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST
          minHeight: '52px',
          '&:active': {
            backgroundColor: 'rgba(0,0,0,0.08)',
          },
          // Desktop
          '@media (min-width: 600px)': {
            minHeight: '48px',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST
          minHeight: '44px',
          minWidth: '72px',
          padding: '8px 12px',
          fontSize: '0.8rem',
          // Desktop
          '@media (min-width: 600px)': {
            minHeight: '48px',
            minWidth: '80px',
            padding: '12px 16px',
            fontSize: '0.875rem',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST
          minHeight: '44px',
          // Desktop
          '@media (min-width: 600px)': {
            minHeight: '48px',
          },
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST
          height: '60px',
          // Desktop
          '@media (min-width: 600px)': {
            height: '64px',
          },
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST
          minWidth: '56px',
          padding: '4px 8px 6px',
          '& .MuiSvgIcon-root': {
            fontSize: '1.4rem',
          },
          // Desktop
          '@media (min-width: 600px)': {
            minWidth: '64px',
            padding: '6px 12px 8px',
            '& .MuiSvgIcon-root': {
              fontSize: '1.5rem',
            },
          },
        },
        label: {
          // MOBILE-FIRST
          fontSize: '0.65rem',
          // Desktop
          '@media (min-width: 600px)': {
            fontSize: '0.7rem',
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          // MOBILE-FIRST
          fontSize: '0.65rem',
          minWidth: '16px',
          height: '16px',
          padding: '0 4px',
          // Desktop
          '@media (min-width: 600px)': {
            fontSize: '0.7rem',
            minWidth: '18px',
            height: '18px',
          },
        },
      },
    },
    MuiRating: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST
          fontSize: '1.1rem',
          // Desktop: стандартный размер
          '@media (min-width: 600px)': {
            fontSize: '1.5rem',
          },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: компактные отступы
          left: '8px',
          right: '8px',
          bottom: '8px',
          // Desktop: стандартные позиции
          '@media (min-width: 600px)': {
            left: '24px',
            right: 'auto',
            bottom: '24px',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST
          padding: '6px 12px',
          fontSize: '0.85rem',
          // Desktop
          '@media (min-width: 600px)': {
            padding: '6px 16px',
            fontSize: '0.875rem',
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

export default theme;
